import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { growthKeys } from '@/data/queryKeys';
import { useGrowthQuery } from '@/data/useGrowthQuery';
import { useNotify } from '@/data/notifications';
import {
  contactSetupWebmaster,
  fetchSetupChecklist,
  markSetupTaskDone,
  skipSetupTask,
} from '../api';

function setupMutationError(notify: ReturnType<typeof useNotify>, error: unknown, fallback: string) {
  if (isAxiosError(error) && error.response?.status === 422) {
    const body = error.response.data as {
      error?: { code?: string; message?: string; details?: { missing_dependencies?: string[] } };
    };
    if (body.error?.code === 'dependency_blocked') {
      const deps = body.error.details?.missing_dependencies;
      notify.error(
        error,
        deps?.length ? `Complete these first: ${deps.join(', ')}` : 'Complete prerequisite tasks first',
      );
      return;
    }
  }
  notify.error(error, fallback);
}

export function useSetupChecklist() {
  return useGrowthQuery({
    queryKey: growthKeys.setupChecklist(),
    queryFn: fetchSetupChecklist,
  });
}

export function useSetupChecklistMutations() {
  const queryClient = useQueryClient();
  const notify = useNotify();

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: growthKeys.setupChecklist() });
  };

  const markDone = useMutation({
    mutationFn: ({ taskKey }: { taskKey: string }) => markSetupTaskDone(taskKey),
    onSuccess: () => {
      notify.success('Task marked done');
      invalidate();
    },
    onError: (e) => setupMutationError(notify, e, 'Could not mark task done'),
  });

  const skipTask = useMutation({
    mutationFn: ({ taskKey }: { taskKey: string }) => skipSetupTask(taskKey),
    onSuccess: () => {
      notify.success('Task skipped');
      invalidate();
    },
    onError: (e) => setupMutationError(notify, e, 'Could not skip task'),
  });

  const contactWebmaster = useMutation({
    mutationFn: (message: string) => contactSetupWebmaster(message),
    onSuccess: () => {
      notify.success('Request sent', 'ELVA will reach out to your webmaster');
      invalidate();
    },
    onError: (e) => notify.error(e, 'Could not contact webmaster'),
  });

  return { markDone, skipTask, contactWebmaster };
}
